/**
 * Home Page Component - IT Engine Sparks
 * Refactored: Clean, modular architecture with feature-sliced design
 *
 * Architecture:
 * - Main orchestrator component (no business logic)
 * - All sections extracted to separate components
 * - All data extracted to constants
 * - Easy to maintain and test
 */
import { useState } from "react";
import { Box } from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  HeroSection,
  StatsSection,
  ServicesSection,
  BenefitsSection,
  TestimonialsSection,
  FAQSection,
  CTASection,
} from "./sections";

const Home = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": t("home.faq.question1.q"),
        "acceptedAnswer": { "@type": "Answer", "text": t("home.faq.question1.a") }
      },
      {
        "@type": "Question",
        "name": t("home.faq.question2.q"),
        "acceptedAnswer": { "@type": "Answer", "text": t("home.faq.question2.a") }
      },
      {
        "@type": "Question",
        "name": t("home.faq.question3.q"),
        "acceptedAnswer": { "@type": "Answer", "text": t("home.faq.question3.a") }
      },
      {
        "@type": "Question",
        "name": t("home.faq.question4.q"),
        "acceptedAnswer": { "@type": "Answer", "text": t("home.faq.question4.a") }
      }
    ]
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Box>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t("home.title")}</title>
        <meta name="description" content={t("home.description")} />
        <meta property="og:title" content={t("home.ogTitle")} />
        <meta property="og:description" content={t("home.ogDescription")} />
        <meta property="og:image" content="https://itenginesparks.com/og-banner.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="IT Engine Sparks — Web & Software Development" />
        <meta property="og:url" content="https://itenginesparks.com/" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://itenginesparks.com/" />
        <meta name="twitter:title" content={t("home.ogTitle")} />
        <meta name="twitter:description" content={t("home.description")} />
        <meta name="twitter:image" content="https://itenginesparks.com/og-banner.png" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Page Sections */}
      <HeroSection expanded={expanded} onToggleExpanded={handleToggleExpanded} />
      <StatsSection />
      <ServicesSection />
      <BenefitsSection />
      <TestimonialsSection />
      <FAQSection />
      <CTASection />

      {/* Footer placeholder */}
      <Box sx={{ backgroundColor: "primary.dark", py: 3, textAlign: "center" }} />
    </Box>
  );
};

export default Home;
